import { routes } from '@/lib';
import { capitalize } from '@/utils';

import type { TypeHitZone } from '@/types';
import type { TypeFlat } from '@/types/analytics/flat';

export const flatName = (hit: any) =>
  `${hit?.tipoDeUsoDescription} de ${hit?.habitaciones} ${
    (hit?.habitaciones as number) > 1 ? 'habitaciones' : 'habitación'
  } en ${capitalize(hit?.STREET) ?? ''}(${
    capitalize(hit?.tipoZona?.infoAdicional as string) ??
    capitalize(hit?.ciudad as string) ??
    ''
  })`;

export const convertToURL = (string: string) =>
  string
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll('-', ' ')
    .replace(/\s\s+/g, ' ')
    .replaceAll(' ', '-')
    .replaceAll('(', '')
    .replaceAll(')', '');

const zone: { [key in TypeFlat['ventaAlquiler']]: (zone: string) => string } = {
  MED_EST: routes.flats.half.zone,
  ALQUILER: routes.flats.rent.zone,
  VENTA: routes.flats.sale.zone,
  ALQ_VENT: routes.flats.half.zone,
};

export const zonePath = (hit: TypeHitZone, type: TypeFlat['ventaAlquiler']) =>
  convertToURL(
    zone[type ?? 'MED_EST'](
      `${hit?.provincia ? hit.provincia : ''}${
        hit?.ciudad ? `/${hit.ciudad}` : ''
      }${hit?.zona ? `/${hit.zona}` : ''}${hit?.calle ? `/${hit.calle}` : ''}`,
    ),
  );

export const flatPath = (hit: any) =>
  routes.flats.flat(
    `${convertToURL(flatName(hit))}-${hit?.codigoUnidadAlquiler.replaceAll(
      '/',
      '-',
    )}`,
  );

export const getLastRefined = (items: any, defaultValue: string): string => {
  const item = items?.filter((item: any) => item.isRefined)[0];

  if (item && item.isRefined) {
    const hasChildrenRefined =
      item?.data?.filter((item: any) => item.isRefined)?.length > 0;

    if (hasChildrenRefined && item?.data) {
      return getLastRefined(item.data, defaultValue);
    } else {
      return item?.value;
    }
  } else {
    return defaultValue;
  }
};

export const getLastRefinedItem = (items: any): any => {
  const item = items?.filter((item: any) => item.isRefined)[0];

  if (item && item.isRefined) {
    const hasChildrenRefined =
      item?.data?.filter((item: any) => item.isRefined)?.length > 0;

    if (hasChildrenRefined && item?.data) {
      return getLastRefinedItem(item.data);
    } else {
      return item;
    }
  } else {
    return null;
  }
};

export const sortZonasResults = (hits: any) => {
  if (!hits) return [];
  return [...hits].sort((a: any, b: any) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
};

/**
 * Buckets de precio compartidos por los selects min/max del listado
 * (`ListOfFlats`) y el dropdown de Precio Máx del hero (`SearchBoxHero`).
 * Cualquier cambio en los pasos o el número de buckets se refleja en ambos
 * sitios automáticamente.
 *
 * Reglas heredadas del listado: 15 valores; venta = `(i+1)·2·75 000`
 * (150k, 300k, …, 2.25M €); alquiler / temporal = `(i+2)·250`
 * (500, 750, …, 4 000 €/mes).
 */
export const PRICE_FILTER_STEP_RENT = 250;
export const PRICE_FILTER_STEP_SALE = 75000;
export const PRICE_FILTER_COUNT = 15;

export const priceFilterOptions = (
  type: TypeFlat['ventaAlquiler'] | undefined,
): number[] =>
  Array.from({ length: PRICE_FILTER_COUNT }, (_, i) =>
    type === 'VENTA'
      ? (i + 1) * 2 * PRICE_FILTER_STEP_SALE
      : (i + 1) * PRICE_FILTER_STEP_RENT + PRICE_FILTER_STEP_RENT,
  );

/**
 * Conectores que van en minúscula dentro de un topónimo ("Alcalá de
 * Henares", "Arroyo del Cañaveral").
 */
const LOWERCASE_ZONE_WORDS = new Set([
  'de',
  'del',
  'la',
  'las',
  'los',
  'el',
  'y',
  'a',
  'en',
]);

export const titleCaseZone = (segment: string) =>
  segment
    .toLowerCase()
    .split(' ')
    .map((word, i) =>
      i > 0 && LOWERCASE_ZONE_WORDS.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ')
    // También tras un paréntesis pegado: "Cañaveral(cañaveral)" →
    // "Cañaveral(Cañaveral)".
    .replace(/\(\p{Ll}/gu, (m) => m.toUpperCase());

/**
 * Presenta un valor jerárquico de zona de Algolia ("Madrid > ALCALÁ DE
 * HENARES") como texto legible: casing normalizado por palabra, segmentos
 * duplicados consecutivos colapsados y los hijos entre paréntesis →
 * "Madrid (Alcalá de Henares)". Un único segmento se devuelve tal cual
 * normalizado.
 */
export const formatZoneName = (value?: string | null): string => {
  if (!value) return '';
  const segments = value
    .split(' > ')
    .map((s) => titleCaseZone(s.trim()))
    .filter((s, i, arr) => s.length > 0 && (i === 0 || s !== arr[i - 1]));
  if (segments.length === 0) return '';
  const [head, ...rest] = segments;
  return rest.length > 0 ? `${head} (${rest.join(', ')})` : head;
};
